/**
 * OneSignal Service
 *
 * Servizio per la gestione delle notifiche push tramite OneSignal SDK 5.x
 * Questo servizio utilizza le nuove API user-centric di OneSignal.
 *
 * Documentazione: https://documentation.onesignal.com/docs/ionic-capacitor-cordova-sdk-setup
 */

import capacitorService from './capacitor-service';

// L'App ID di OneSignal per il progetto
const ONESIGNAL_APP_ID = 'b60b732b-4fbc-4567-91f0-4af1861bbfaf';

class OneSignalService {
  constructor() {
    this._initialized = false;
    this._notificationOpenedHandler = null;
    this._notificationReceivedHandler = null;
    this._permissionChangedHandler = null;
  }

  /**
   * Verifica se OneSignal è disponibile
   */
  get isAvailable() {
    return capacitorService.isNative && typeof window.plugins !== 'undefined' && window.plugins.OneSignal;
  }

  /**
   * Ottiene l'istanza di OneSignal
   */
  get oneSignal() {
    return this.isAvailable ? window.plugins.OneSignal : null;
  }

  /**
   * Inizializza OneSignal
   * Da chiamare una volta che l'app è pronta (deviceready/DOMContentLoaded)
   */
  async initialize() {
    if (!this.isAvailable) {
      console.log('OneSignal: Non disponibile su questa piattaforma');
      return false;
    }

    if (this._initialized) {
      console.log('OneSignal: Già inizializzato');
      return true;
    }

    try {
      const OneSignal = this.oneSignal;

      // Inizializza con l'App ID
      OneSignal.initialize(ONESIGNAL_APP_ID);

      // Configura il log level per debug (rimuovere in produzione)
      if (process.env.NODE_ENV === 'development') {
        OneSignal.Debug.setLogLevel(6); // VERBOSE
      } else {
        OneSignal.Debug.setLogLevel(0); // NONE
      }

      this._initialized = true;
      console.log('OneSignal: Inizializzato con successo');

      return true;
    } catch (error) {
      console.error('OneSignal: Errore durante l\'inizializzazione:', error);
      return false;
    }
  }

  /**
   * Richiede il permesso per le notifiche push (iOS)
   * Su Android 13+ (API 33) richiede anche il permesso POST_NOTIFICATIONS
   */
  async requestPermission() {
    if (!this.isAvailable) return false;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa Notifications.requestPermission
      const accepted = await OneSignal.Notifications.requestPermission(true);
      console.log('OneSignal: Permesso notifiche:', accepted ? 'accettato' : 'rifiutato');
      return accepted;
    } catch (error) {
      console.error('OneSignal: Errore richiesta permesso:', error);
      return false;
    }
  }

  /**
   * Verifica se il permesso per le notifiche è stato concesso
   */
  async hasPermission() {
    if (!this.isAvailable) return false;

    try {
      const OneSignal = this.oneSignal;
      return OneSignal.Notifications.hasPermission();
    } catch (error) {
      console.error('OneSignal: Errore verifica permesso:', error);
      return false;
    }
  }

  /**
   * Imposta l'ID esterno dell'utente per il targeting
   * Questo sostituisce setExternalUserId delle versioni precedenti
   *
   * @param {string} externalId - L'ID esterno dell'utente (es: uuid_userId)
   */
  async login(externalId) {
    if (!this.isAvailable || !externalId) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa login() invece di setExternalUserId()
      await OneSignal.login(externalId);

      console.log('OneSignal: Login effettuato con ID:', externalId);
    } catch (error) {
      console.error('OneSignal: Errore login:', error);
    }
  }

  /**
   * Rimuove l'ID esterno dell'utente
   * Questo sostituisce removeExternalUserId delle versioni precedenti
   */
  async logout() {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa logout()
      await OneSignal.logout();

      console.log('OneSignal: Logout effettuato');
    } catch (error) {
      console.error('OneSignal: Errore logout:', error);
    }
  }

  /**
   * Aggiunge un tag all'utente
   *
   * @param {string} key - La chiave del tag
   * @param {string} value - Il valore del tag
   */
  async addTag(key, value) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.addTag()
      OneSignal.User.addTag(key, value);

      console.log('OneSignal: Tag aggiunto:', key, value);
    } catch (error) {
      console.error('OneSignal: Errore aggiunta tag:', error);
    }
  }

  /**
   * Aggiunge più tag all'utente
   *
   * @param {Object} tags - Oggetto con coppie chiave-valore
   */
  async addTags(tags) {
    if (!this.isAvailable || !tags) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.addTags()
      OneSignal.User.addTags(tags);

      console.log('OneSignal: Tags aggiunti:', tags);
    } catch (error) {
      console.error('OneSignal: Errore aggiunta tags:', error);
    }
  }

  /**
   * Rimuove un tag dall'utente
   *
   * @param {string} key - La chiave del tag da rimuovere
   */
  async removeTag(key) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.removeTag()
      OneSignal.User.removeTag(key);

      console.log('OneSignal: Tag rimosso:', key);
    } catch (error) {
      console.error('OneSignal: Errore rimozione tag:', error);
    }
  }

  /**
   * Imposta l'email dell'utente
   *
   * @param {string} email - L'email dell'utente
   */
  async setEmail(email) {
    if (!this.isAvailable || !email) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.addEmail()
      OneSignal.User.addEmail(email);

      console.log('OneSignal: Email impostata:', email);
    } catch (error) {
      console.error('OneSignal: Errore impostazione email:', error);
    }
  }

  /**
   * Rimuove l'email dell'utente
   *
   * @param {string} email - L'email da rimuovere
   */
  async removeEmail(email) {
    if (!this.isAvailable || !email) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.removeEmail()
      OneSignal.User.removeEmail(email);

      console.log('OneSignal: Email rimossa:', email);
    } catch (error) {
      console.error('OneSignal: Errore rimozione email:', error);
    }
  }

  /**
   * Imposta il numero di telefono dell'utente (SMS)
   *
   * @param {string} phone - Il numero di telefono con prefisso internazionale
   */
  async setPhoneNumber(phone) {
    if (!this.isAvailable || !phone) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.addSms()
      OneSignal.User.addSms(phone);

      console.log('OneSignal: Telefono impostato:', phone);
    } catch (error) {
      console.error('OneSignal: Errore impostazione telefono:', error);
    }
  }

  /**
   * Imposta un handler per quando una notifica viene aperta
   *
   * @param {Function} handler - Funzione callback che riceve i dati della notifica
   */
  setNotificationOpenedHandler(handler) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      this._notificationOpenedHandler = handler;

      // OneSignal 5.x usa Notifications.addEventListener con 'click'
      OneSignal.Notifications.addEventListener('click', (event) => {
        console.log('OneSignal: Notifica aperta:', event);

        if (this._notificationOpenedHandler) {
          // Estrai i dati dalla notifica
          const notification = event.notification;
          const additionalData = notification.additionalData || {};

          this._notificationOpenedHandler({
            notification: notification,
            additionalData: additionalData,
            action: event.action,
          });
        }
      });

      console.log('OneSignal: Handler notifica aperta registrato');
    } catch (error) {
      console.error('OneSignal: Errore registrazione handler:', error);
    }
  }

  /**
   * Imposta un handler per quando una notifica viene ricevuta (foreground)
   *
   * @param {Function} handler - Funzione callback che riceve i dati della notifica
   */
  setNotificationReceivedHandler(handler) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      this._notificationReceivedHandler = handler;

      // OneSignal 5.x usa Notifications.addEventListener con 'foregroundWillDisplay'
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
        console.log('OneSignal: Notifica ricevuta in foreground:', event);

        const notification = event.notification;

        if (this._notificationReceivedHandler) {
          this._notificationReceivedHandler({
            notification: notification,
            additionalData: notification.additionalData || {},
          });
        }

        // Mostra la notifica (puoi anche nasconderla con event.preventDefault())
        event.getNotification().display();
      });

      console.log('OneSignal: Handler notifica ricevuta registrato');
    } catch (error) {
      console.error('OneSignal: Errore registrazione handler:', error);
    }
  }

  /**
   * Imposta un handler per i cambiamenti di permesso delle notifiche
   *
   * @param {Function} handler - Funzione callback
   */
  setPermissionChangedHandler(handler) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      this._permissionChangedHandler = handler;

      // OneSignal 5.x usa Notifications.addEventListener con 'permissionChange'
      OneSignal.Notifications.addEventListener('permissionChange', (permission) => {
        console.log('OneSignal: Permesso cambiato:', permission);

        if (this._permissionChangedHandler) {
          this._permissionChangedHandler(permission);
        }
      });

      console.log('OneSignal: Handler permesso registrato');
    } catch (error) {
      console.error('OneSignal: Errore registrazione handler permesso:', error);
    }
  }

  /**
   * Ottiene l'ID del player OneSignal (Push Subscription ID)
   * Nota: In OneSignal 5.x, questo è chiamato Subscription ID
   */
  async getSubscriptionId() {
    if (!this.isAvailable) return null;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa PushSubscription.id
      const id = OneSignal.User.pushSubscription.id;

      return id;
    } catch (error) {
      console.error('OneSignal: Errore ottenimento subscription ID:', error);
      return null;
    }
  }

  /**
   * Verifica se l'utente è iscritto alle notifiche push
   */
  async isSubscribed() {
    if (!this.isAvailable) return false;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa PushSubscription.optedIn
      return OneSignal.User.pushSubscription.optedIn;
    } catch (error) {
      console.error('OneSignal: Errore verifica sottoscrizione:', error);
      return false;
    }
  }

  /**
   * Abilita/disabilita le notifiche push per l'utente
   *
   * @param {boolean} enable - true per abilitare, false per disabilitare
   */
  async setSubscription(enable) {
    if (!this.isAvailable) return;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa PushSubscription.optIn() / optOut()
      if (enable) {
        await OneSignal.User.pushSubscription.optIn();
      } else {
        await OneSignal.User.pushSubscription.optOut();
      }

      console.log('OneSignal: Sottoscrizione:', enable ? 'abilitata' : 'disabilitata');
    } catch (error) {
      console.error('OneSignal: Errore cambio sottoscrizione:', error);
    }
  }

  /**
   * Ottiene l'OneSignal ID dell'utente
   */
  async getOneSignalId() {
    if (!this.isAvailable) return null;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.getOnesignalId()
      const id = await OneSignal.User.getOnesignalId();

      return id;
    } catch (error) {
      console.error('OneSignal: Errore ottenimento OneSignal ID:', error);
      return null;
    }
  }

  /**
   * Ottiene l'External ID dell'utente
   */
  async getExternalId() {
    if (!this.isAvailable) return null;

    try {
      const OneSignal = this.oneSignal;

      // OneSignal 5.x usa User.getExternalId()
      const id = await OneSignal.User.getExternalId();

      return id;
    } catch (error) {
      console.error('OneSignal: Errore ottenimento External ID:', error);
      return null;
    }
  }
}

// Esporta un'istanza singleton
const oneSignalService = new OneSignalService();
export default oneSignalService;

// Esporta anche la classe per testing
export { OneSignalService };
